import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { AiOutlineClose, AiOutlineCopy, AiOutlineDelete } from "react-icons/ai";

import { useTelegramIntegrationService } from "../services/api";
import { useNotification } from "../hooks/useNotification";
import EmptyState from "../components/EmptyState";
import { formatDateTime } from "../utils/dateUtils";
import { ENV } from "../config/env";

/**
 * Pantalla "Conectar Telegram" — FSD-001 + ADR-013 §"Spec de UI".
 *
 * Funcionalidad:
 *  - Generar un link de pairing (deep link + QR + botón copiar).
 *  - Listar los chats vinculados del usuario.
 *  - Desvincular un chat con confirmación.
 *
 * Protegida por ProtectedRoute con rol IMPORTER (ver App.jsx).
 */
const TelegramIntegrationPage = () => {
    const navigate = useNavigate();
    const tgService = useTelegramIntegrationService();
    const notify = useNotification();

    // Estado del link de pairing recién generado (null = no hay link activo)
    const [pairingLink, setPairingLink] = useState(null);  // { deepLinkUrl, expiresAt }
    const [generatingLink, setGeneratingLink] = useState(false);

    // Estado de la lista de bindings
    const [bindings, setBindings] = useState([]);
    const [loadingBindings, setLoadingBindings] = useState(true);

    // Modal de confirmación para desvincular
    const [bindingToRemove, setBindingToRemove] = useState(null);

    // Countdown regresivo para pairingLink.expiresAt (P2 #72)
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    useEffect(() => {
        if (!pairingLink?.expiresAt) {
            setRemainingSeconds(0);
            return undefined;
        }
        const tick = () => {
            const diff = Math.max(0, Math.floor((new Date(pairingLink.expiresAt).getTime() - Date.now()) / 1000));
            setRemainingSeconds(diff);
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [pairingLink]);

    const formatCountdown = (secs) => {
        if (secs <= 0) return 'Expirado';
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${String(s).padStart(2, '0')}`;
    };

    const loadBindings = async () => {
        setLoadingBindings(true);
        try {
            const response = await tgService.listBindings();
            setBindings(response.data || []);
        } catch (error) {
            notify.error("Error al cargar los chats vinculados");
        } finally {
            setLoadingBindings(false);
        }
    };

    useEffect(() => {
        // Carga inicial al montar. Evitamos meter tgService/notify en deps porque
        // se recrean en cada render; el patrón es cargar una sola vez y volver a
        // llamar explícitamente tras acciones (desvincular, cerrar link de pairing).
        loadBindings();
    }, []); // eslint-disable-line

    const handleGenerateLink = async () => {
        setGeneratingLink(true);
        try {
            const response = await tgService.initiatePairing();
            setPairingLink(response.data);
        } catch (error) {
            notify.error("No se pudo generar el link. Intentá de nuevo en unos segundos.");
        } finally {
            setGeneratingLink(false);
        }
    };

    const handleCopyLink = () => {
        if (!pairingLink?.deepLinkUrl) return;
        navigator.clipboard.writeText(pairingLink.deepLinkUrl);
        notify.success("Link copiado al portapapeles");
    };

    const handleClosePairingLink = () => {
        setPairingLink(null);
        // Refresco por si el usuario ya hizo el pairing en otra ventana
        loadBindings();
    };

    const openConfirmRemove = (binding) => {
        setBindingToRemove(binding);
    };

    const handleConfirmRemove = async () => {
        if (!bindingToRemove) return;
        try {
            await tgService.removeBinding(bindingToRemove.id);
            notify.success("Chat desvinculado correctamente");
            setBindingToRemove(null);
            loadBindings();
        } catch (error) {
            notify.error("Error al desvincular el chat");
        }
    };

    const formatDate = (isoString) => {
        return isoString ? formatDateTime(isoString, { placeholder: "—" }) : "—";
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Conectar Telegram</h1>
                <button style={styles.closeButton} onClick={() => navigate("/")}>
                    <AiOutlineClose />
                </button>
            </div>

            <p style={styles.description}>
                Vinculá tu chat de Telegram con el bot de {ENV.APP_NAME} para cargar documentos desde el celular.
                Al vincular se emite una API key específica de este canal con permisos de importación.
            </p>

            {/* Sección 1 — Generar link */}
            <section style={styles.section}>
                <h2 style={styles.sectionTitle}>Nueva vinculación</h2>

                {!pairingLink && (
                    <button
                        style={styles.primaryButton}
                        onClick={handleGenerateLink}
                        disabled={generatingLink}
                    >
                        {generatingLink ? "Generando..." : "Generar link de pairing"}
                    </button>
                )}

                {pairingLink && (
                    <div style={styles.pairingBox}>
                        <p style={styles.pairingInstruction}>
                            Abrí este link en Telegram (o escaneá el QR desde otro dispositivo).
                            El link expira en 10 minutos y se puede usar una sola vez.
                        </p>

                        <div style={styles.qrWrap}>
                            <QRCodeSVG value={pairingLink.deepLinkUrl} size={180} />
                        </div>

                        <div style={styles.linkDisplay}>
                            <code style={styles.linkCode}>{pairingLink.deepLinkUrl}</code>
                            <button style={styles.copyButton} onClick={handleCopyLink}>
                                <AiOutlineCopy /> Copiar
                            </button>
                        </div>

                        <p
                            style={{
                                ...styles.expiresHint,
                                color: remainingSeconds <= 60 ? '#D97706' : styles.expiresHint.color,
                                fontWeight: remainingSeconds <= 60 ? 600 : 400,
                            }}
                            aria-live="polite"
                        >
                            Expira en {formatCountdown(remainingSeconds)}
                        </p>

                        <button style={styles.secondaryButton} onClick={handleClosePairingLink}>
                            Listo, ya lo abrí
                        </button>
                    </div>
                )}
            </section>

            {/* Sección 2 — Chats vinculados */}
            <section style={styles.section}>
                <h2 style={styles.sectionTitle}>Chats vinculados</h2>

                {loadingBindings ? (
                    <p style={styles.loadingText}>Cargando...</p>
                ) : bindings.length === 0 ? (
                    <EmptyState message="Todavía no vinculaste ningún chat de Telegram. Generá un link de pairing para empezar." />
                ) : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Usuario Telegram</th>
                                <th style={styles.th}>Vinculado el</th>
                                <th style={styles.th}>Último uso</th>
                                <th style={styles.th}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bindings.map((b) => (
                                <tr key={b.id}>
                                    <td style={styles.td}>
                                        {b.telegramUsername || `(id: ${b.telegramUserId})`}
                                    </td>
                                    <td style={styles.td}>{formatDate(b.createdAt)}</td>
                                    <td style={styles.td}>{formatDate(b.lastSeenAt)}</td>
                                    <td style={styles.td}>
                                        <button
                                            style={styles.dangerButton}
                                            onClick={() => openConfirmRemove(b)}
                                            title="Desvincular este chat"
                                        >
                                            <AiOutlineDelete /> Desvincular
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Modal de confirmación */}
            {bindingToRemove && (
                <div style={styles.modalOverlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h3>Desvincular chat</h3>
                            <button
                                style={styles.closeButton}
                                onClick={() => setBindingToRemove(null)}
                            >
                                <AiOutlineClose />
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <p>
                                Esto cerrará la sesión del bot en el chat de
                                <strong> {bindingToRemove.telegramUsername || `id ${bindingToRemove.telegramUserId}`}</strong>.
                                La API key asociada se revoca inmediatamente. ¿Continuar?
                            </p>
                        </div>
                        <div style={styles.modalFooter}>
                            <button
                                style={styles.cancelButton}
                                onClick={() => setBindingToRemove(null)}
                            >
                                Cancelar
                            </button>
                            <button style={styles.dangerButton} onClick={handleConfirmRemove}>
                                Desvincular
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        maxWidth: "900px",
        margin: "0 auto",
        padding: "24px",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    },
    title: {
        fontSize: "24px",
        fontWeight: "700",
        color: "#111827",
        margin: 0,
    },
    closeButton: {
        background: "none",
        border: "none",
        fontSize: "24px",
        cursor: "pointer",
        color: "#9CA3AF",
    },
    description: {
        color: "#6B7280",
        marginBottom: "24px",
        lineHeight: 1.5,
    },
    section: {
        backgroundColor: "white",
        padding: "20px",
        borderRadius: "8px",
        border: "1px solid #E5E7EB",
        marginBottom: "20px",
    },
    sectionTitle: {
        fontSize: "18px",
        fontWeight: "600",
        color: "#111827",
        marginTop: 0,
        marginBottom: "16px",
    },
    primaryButton: {
        padding: "12px 20px",
        backgroundColor: "#00468B",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
    },
    secondaryButton: {
        padding: "10px 16px",
        backgroundColor: "white",
        color: "#374151",
        border: "1px solid #D1D5DB",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
        marginTop: "8px",
    },
    dangerButton: {
        padding: "8px 14px",
        backgroundColor: "#EF4444",
        color: "white",
        border: "none",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
    },
    pairingBox: {
        padding: "16px",
        backgroundColor: "#F9FAFB",
        borderRadius: "8px",
        border: "1px dashed #D1D5DB",
    },
    pairingInstruction: {
        color: "#374151",
        marginTop: 0,
        marginBottom: "16px",
    },
    qrWrap: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "16px",
    },
    linkDisplay: {
        display: "flex",
        alignItems: "stretch",
        borderRadius: "6px",
        overflow: "hidden",
        border: "1px solid #D1D5DB",
        marginBottom: "8px",
    },
    linkCode: {
        flex: 1,
        padding: "10px 12px",
        backgroundColor: "#F3F4F6",
        fontFamily: "monospace",
        fontSize: "13px",
        color: "#111827",
        wordBreak: "break-all",
    },
    copyButton: {
        backgroundColor: "#00468B",
        color: "white",
        border: "none",
        padding: "0 16px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: "6px",
        fontSize: "13px",
        fontWeight: "600",
    },
    expiresHint: {
        fontSize: "12px",
        color: "#6B7280",
        marginBottom: "8px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    th: {
        textAlign: "left",
        padding: "10px 12px",
        borderBottom: "2px solid #E5E7EB",
        fontSize: "13px",
        fontWeight: "600",
        color: "#374151",
        backgroundColor: "#F9FAFB",
    },
    td: {
        padding: "12px",
        borderBottom: "1px solid #E5E7EB",
        fontSize: "14px",
        color: "#111827",
    },
    loadingText: {
        color: "#6B7280",
        fontStyle: "italic",
    },
    modalOverlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 2000,
    },
    modal: {
        backgroundColor: "white",
        borderRadius: "12px",
        width: "90%",
        maxWidth: "480px",
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    },
    modalHeader: {
        padding: "16px 20px",
        borderBottom: "1px solid #E5E7EB",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    modalBody: {
        padding: "20px",
        color: "#374151",
        lineHeight: 1.5,
    },
    modalFooter: {
        padding: "16px 20px",
        borderTop: "1px solid #E5E7EB",
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
    },
    cancelButton: {
        padding: "10px 16px",
        backgroundColor: "white",
        color: "#374151",
        border: "1px solid #D1D5DB",
        borderRadius: "6px",
        fontSize: "14px",
        fontWeight: "600",
        cursor: "pointer",
    },
};

export default TelegramIntegrationPage;
